import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useApi from "../hook/useApi";
import Loader from "./Loader/Loader";
import { formatCurrency, numberToWords } from "../utils/helper";
import { Icon } from "@iconify/react";

const SubadminFormLayer = ({
  mode = "create",
  initialData = null,
  id = null,
}) => {
  const isEditMode = mode === "update";
  const navigate = useNavigate();
  const { AsyncPatchApiCall, AsyncPostApiCall, AsyncGetApiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [segments, setSegments] = useState([]);
  const [isLoginIdValid, setIsLoginIdValid] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    loginId: "",
    password: "",
    masterName: "",
    remarks: "",
    partnership: "",
    onlyPosition: false,
    limitOrders: true,
    segmentSettings: {},
  });
  const [showPassword, setShowPassword] = useState(false);

  const fetchSegments = async () => {
    try {
      const result = await AsyncGetApiCall(
        "/trading-settings/segments/list?pageNo=1&limitVal=100",
      );
      if (result?.success) {
        setSegments(result.data || []);
      } else {
        toast.error(result?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Error fetching segments:", error);
    }
  };

  useEffect(() => {
    fetchSegments();
  }, []);

  useEffect(() => {
    if (isEditMode && initialData) {
      setFormData({
        loginId: initialData.loginId || "",
        password: "",
        masterName: initialData.masterName || "",
        remarks: initialData.remarks ?? "",
        partnership:
          initialData.partnership !== undefined &&
            initialData.partnership !== null
            ? String(initialData.partnership)
            : "",
        onlyPosition: Boolean(initialData.onlyPosition),
        limitOrders:
          initialData.limitOrders !== undefined
            ? Boolean(initialData.limitOrders)
            : true,
        segmentSettings: initialData.segmentSettings || {},
      });
    }
  }, [isEditMode, initialData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "loginId") {
      if (value.length === 6) {
        checkLoginIdExists(value);
      } else {
        setIsLoginIdValid(false);
        setErrors((prev) => ({ ...prev, loginId: "" }));
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSegmentSettingChange = (segmentName, field, value) => {
    setFormData((prev) => ({
      ...prev,
      segmentSettings: {
        ...prev.segmentSettings,
        [segmentName]: {
          ...(prev.segmentSettings[segmentName] || {
            enabled: false,
            marginLimit: "",
            maxUserMarginLimit: "",
            brokerageType: detectBrokerageType(segmentName),
            deliveryBrokerage: "",
            intradayBrokerage: "",
          }),
          [field]: value,
        },
      },
    }));
  };

  const detectBrokerageType = (name) => {
    const n = name.toUpperCase();
    if (
      n.includes("NSE") ||
      n.includes("NFO") ||
      n.includes("FOREX") ||
      n.includes("CURRENCY")
    ) {
      return "percentage";
    }
    if (n.includes("MCX") || n.includes("COMEX")) {
      return "flat";
    }
    return "percentage";
  };

  const generateLoginId = async () => {
    const result = await AsyncGetApiCall("/users/generate-login-id");
    if (result?.data?.loginId) {
      // formik.setFieldValue("loginId", result.data.loginId, true);
      setFormData((prev) => ({ ...prev, loginId: result.data.loginId }));
      setIsLoginIdValid(true);
    }
  };

  const checkLoginIdExists = async (val) => {
    if (!val || val.length !== 6) {
      setIsLoginIdValid(null);
      return;
    }
    const result = await AsyncGetApiCall("/subadmin/check-login-id", {
      loginId: val,
    });
    if (result?.data?.exists) {
      setIsLoginIdValid(false);
      setErrors((prev) => ({ ...prev, loginId: "Login ID already exists" }));
    } else {
      setIsLoginIdValid(true);
      setErrors((prev) => ({ ...prev, loginId: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const partnershipNum = Number(formData.partnership);
    if (
      formData.partnership === "" ||
      Number.isNaN(partnershipNum) ||
      partnershipNum < 0
    ) {
      toast.error("Enter a valid partnership value (0 or greater)");
      return;
    }

    if (!isEditMode && !formData.password?.trim()) {
      toast.error("Password is required");
      return;
    } else if (formData.password?.trim()) {
      if (formData.password.trim().length < 6) {
        toast.error("Password must be at least 6 characters long");
        return;
      } else if (!/^[0-9]+$/.test(formData.password.trim())) {
        toast.error("Only numbers are allowed");
        return;
      }
    }

    setLoading(true);

    try {
      // Filter only enabled segments and sanitize numeric values
      const sanitizedSegmentSettings = {};
      Object.keys(formData.segmentSettings).forEach((key) => {
        const seg = formData.segmentSettings[key];
        if (seg.enabled) {
          sanitizedSegmentSettings[key] = {
            ...seg,
            marginLimit: Number(seg.marginLimit) || 0,
            maxUserMarginLimit: Number(seg.maxUserMarginLimit) || 0,
            deliveryBrokerage: Number(seg.deliveryBrokerage) || 0,
            intradayBrokerage: Number(seg.intradayBrokerage) || 0,
          };
        }
      });

      const basePayload = {
        loginId: formData.loginId.trim(),
        masterName: formData.masterName.trim(),
        remarks: formData.remarks.trim(),
        partnership: partnershipNum,
        onlyPosition: formData.onlyPosition,
        limitOrders: formData.limitOrders,
        segmentSettings: sanitizedSegmentSettings,
      };

      let endpoint = "/subadmin/create";
      let payload;

      if (isEditMode) {
        endpoint = "/subadmin/update";
        payload = {
          id,
          ...basePayload,
        };
        if (formData.password?.trim()) {
          payload.password = formData.password.trim();
        }
      } else {
        payload = {
          ...basePayload,
          password: formData.password.trim(),
        };
      }

      let result;
      if (isEditMode) {
        result = await AsyncPatchApiCall(endpoint, payload);
      } else {
        result = await AsyncPostApiCall(endpoint, payload);
      }

      if (result?.errors) {
        toast.error(
          Array.isArray(result.errors) ? result.errors[0] : result.errors,
        );
      } else if (result?.success) {
        toast.success(
          result?.message ||
          (isEditMode ? "Subadmin updated" : "Subadmin created successfully"),
        );
        navigate("/subadmin-list");
      } else {
        toast.error(result?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isEditMode) {
      generateLoginId();
    }
  }, []);

  return (
    <>
      {loading && <Loader />}
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24">
          <h5 className="mb-0">
            {isEditMode ? "Update Subadmin" : "Create Subadmin"}
          </h5>
        </div>
        <div className="card-body p-24">
          <form className="row g-3" onSubmit={handleSubmit}>
            <div className="col-md-6">
              <label className="form-label">
                Login ID <span className="text-danger">*</span>
              </label>
              <div className="input-group has-validation">
                <input
                  type="text"
                  className="form-control"
                  {...(!isEditMode && {
                    style: {
                      borderBottomRightRadius: "0",
                      borderTopRightRadius: "0"
                    }
                  })}
                  name="loginId"
                  value={formData.loginId}
                  onChange={handleChange}
                  required
                  autoComplete="username"
                  maxLength={6}
                  disabled={isEditMode}
                />
                {!isEditMode &&
                  <button
                    type="button"
                    onClick={generateLoginId}
                    className="btn btn-outline-primary"
                  >
                    Auto
                  </button>
                }
              </div>
              {isLoginIdValid ? (
                <div className="text-success mt-1 text-xs d-flex align-items-center gap-1">
                  <Icon icon="lucide:check" /> Login ID is available.
                </div>
              ) : errors.loginId ? (
                <div className="text-danger mt-1 text-xs">{errors.loginId}</div>
              ) : (
                <div className="text-secondary-light mt-1 text-xs">
                  Login ID must be exactly 6 digits.
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Password {!isEditMode && <span className="text-danger">*</span>}
                {isEditMode && (
                  <span className="text-secondary-light text-sm">
                    {" "}(leave blank to keep current)
                  </span>
                )}
              </label>
              <div className="input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!isEditMode}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="btn btn-outline-secondary"
                >
                  <Icon icon={showPassword ? "lucide:eye-off" : "lucide:eye"} />
                </button>
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Master name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="masterName"
                value={formData.masterName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Partnership (% share) <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className="form-control"
                name="partnership"
                value={formData.partnership}
                onFocus={(e) =>
                  e.target.value === "0" &&
                  setFormData((prev) => ({ ...prev, partnership: "" }))
                }
                onChange={handleChange}
                min={2}
                max={100}
                step={1}
                required
              />
              <div className="mt-1">
                <span className="text-secondary-light text-sm d-block">
                  Min/Upline: 2% and Max: 100%
                </span>
                <span className="text-success text-sm fw-medium">
                  My Partnership: 100% And Current Master Partnership:{" "}
                  {formData.partnership || 0}%
                </span>
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label d-block">Only Position</label>
              <div className="d-flex gap-3 mt-8">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="onlyPosition"
                    id="onlyPosYes"
                    checked={formData.onlyPosition === true}
                    onChange={() =>
                      setFormData((prev) => ({ ...prev, onlyPosition: true }))
                    }
                  />
                  <label className="form-check-label" htmlFor="onlyPosYes">
                    YES
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="onlyPosition"
                    id="onlyPosNo"
                    checked={formData.onlyPosition === false}
                    onChange={() =>
                      setFormData((prev) => ({ ...prev, onlyPosition: false }))
                    }
                  />
                  <label className="form-check-label" htmlFor="onlyPosNo">
                    NO
                  </label>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label d-block">Limit Orders</label>
              <div className="d-flex gap-3 mt-8">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="limitOrders"
                    id="limitOrdersAllowed"
                    checked={formData.limitOrders === true}
                    onChange={() =>
                      setFormData((prev) => ({ ...prev, limitOrders: true }))
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="limitOrdersAllowed"
                  >
                    Allowed
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="limitOrders"
                    id="limitOrdersNotAllowed"
                    checked={formData.limitOrders === false}
                    onChange={() =>
                      setFormData((prev) => ({ ...prev, limitOrders: false }))
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="limitOrdersNotAllowed"
                  >
                    Not Allowed
                  </label>
                </div>
              </div>
            </div>

            <div className="col-12 mt-40">
              <h6 className="mb-24 border-bottom pb-12 text-primary-light">
                Stock Market Related Calculation (Segments)
              </h6>
              <div className="row g-4">
                {segments.map((segment) => {
                  const settings = formData.segmentSettings[segment.name] || {
                    enabled: false,
                    marginLimit: 0,
                    maxUserMarginLimit: 0,
                    brokerageType: detectBrokerageType(segment.name),
                    deliveryBrokerage: 0,
                    intradayBrokerage: 0,
                  };

                  return (
                    <div className="col-xxl-6 col-md-12" key={segment._id}>
                      <div
                        className={`card radius-12 border ${settings.enabled ? "border-primary-600" : "border-neutral-200"}`}
                      >
                        <div className="card-header py-12 px-20 bg-base border-bottom d-flex align-items-center justify-content-between">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`seg-${segment.name}`}
                              checked={settings.enabled}
                              onChange={(e) =>
                                handleSegmentSettingChange(
                                  segment.name,
                                  "enabled",
                                  e.target.checked,
                                )
                              }
                            />
                            <label
                              className="form-check-label fw-bold"
                              htmlFor={`seg-${segment.name}`}
                            >
                              {segment.displayName || segment.name}
                            </label>
                          </div>
                          {settings.enabled && (
                            <span className="badge bg-primary-600">Active</span>
                          )}
                        </div>
                        {settings.enabled && (
                          <div className="card-body p-20 bg-base">
                            <div className="row g-3">
                              <div className="col-md-6">
                                <label className="form-label text-sm mb-4">
                                  Margin Limit (₹)
                                </label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={settings.marginLimit}
                                  onFocus={(e) =>
                                    e.target.value === "0" &&
                                    handleSegmentSettingChange(
                                      segment.name,
                                      "marginLimit",
                                      "",
                                    )
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val !== "" && Number(val) > 10000000000) return; // Cap at 1000 Crores
                                    handleSegmentSettingChange(
                                      segment.name,
                                      "marginLimit",
                                      val === "" ? "" : Number(val),
                                    );
                                  }}
                                />
                                <div className="mt-1 d-flex flex-column gap-1">
                                  <span className="text-xs text-secondary-light fw-medium">
                                    Formatted:
                                    {formatCurrency(settings.marginLimit)}
                                  </span>
                                  <span className="text-xs text-primary-600 italic">
                                    {numberToWords(settings.marginLimit)}
                                  </span>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <label className="form-label text-sm mb-4">
                                  Max User Margin (₹)
                                </label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={settings.maxUserMarginLimit}
                                  onFocus={(e) =>
                                    e.target.value === "0" &&
                                    handleSegmentSettingChange(
                                      segment.name,
                                      "maxUserMarginLimit",
                                      "",
                                    )
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val !== "" && Number(val) > 10000000000) return; // Cap at 1000 Crores
                                    handleSegmentSettingChange(
                                      segment.name,
                                      "maxUserMarginLimit",
                                      val === "" ? "" : Number(val),
                                    );
                                  }}
                                />
                                <div className="mt-1 d-flex flex-column gap-1">
                                  <span className="text-xs text-secondary-light fw-medium">
                                    Formatted:
                                    {formatCurrency(
                                      settings.maxUserMarginLimit,
                                    )}
                                  </span>
                                  <span className="text-xs text-primary-600 italic">
                                    {numberToWords(settings.maxUserMarginLimit)}
                                  </span>
                                </div>
                              </div>

                              <div className="col-12">
                                <label className="form-label text-sm mb-4">
                                  Brokerage Type
                                </label>
                                <div className="d-flex gap-4">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name={`brokType-${segment.name}`}
                                      id={`per-${segment.name}`}
                                      checked={
                                        settings.brokerageType === "percentage"
                                      }
                                      onChange={() =>
                                        handleSegmentSettingChange(
                                          segment.name,
                                          "brokerageType",
                                          "percentage",
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label text-sm"
                                      htmlFor={`per-${segment.name}`}
                                    >
                                      Turnover (%)
                                    </label>
                                  </div>
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name={`brokType-${segment.name}`}
                                      id={`flat-${segment.name}`}
                                      checked={
                                        settings.brokerageType === "flat"
                                      }
                                      onChange={() =>
                                        handleSegmentSettingChange(
                                          segment.name,
                                          "brokerageType",
                                          "flat",
                                        )
                                      }
                                    />
                                    <label
                                      className="form-check-label text-sm"
                                      htmlFor={`flat-${segment.name}`}
                                    >
                                      Per Lot (₹)
                                    </label>
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-6">
                                <label className="form-label text-sm mb-4">
                                  Min Delivery{" "}
                                  {settings.brokerageType === "percentage"
                                    ? " (%)"
                                    : " (₹)"}
                                </label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  step="any"
                                  value={settings.deliveryBrokerage}
                                  onChange={(e) =>
                                    handleSegmentSettingChange(
                                      segment.name,
                                      "deliveryBrokerage",
                                      e.target.value === "" ? "" : Number(e.target.value),
                                    )
                                  }
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label text-sm mb-4">
                                  Min Intraday{" "}
                                  {settings.brokerageType === "percentage"
                                    ? " (%)"
                                    : " (₹)"}
                                </label>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  step="any"
                                  value={settings.intradayBrokerage}
                                  onChange={(e) =>
                                    handleSegmentSettingChange(
                                      segment.name,
                                      "intradayBrokerage",
                                      e.target.value === "" ? "" : Number(e.target.value),
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="col-12 d-flex justify-content-end gap-2 mt-40 pt-24 border-top">
              <button
                type="button"
                className="btn btn-secondary radius-8"
                onClick={() => navigate("/subadmin-list")}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary radius-8"
                disabled={loading}
              >
                {isEditMode ? "Update" : "Create Master"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default SubadminFormLayer;
